import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import type { ReactNode } from "react";

import { Cascade } from "../../lib/icons";
import { apiClient, type OnboardingStep } from "../../lib/api";

/**
 * Layout chrome shared by every onboarding step.
 *
 * Renders the brand mark, a 4-dot progress indicator (current step
 * highlighted, completed steps dimmed-orange, pending steps muted), and a
 * "Skip onboarding" link in the top-right.
 *
 * "Skip" PATCHes `/api/onboarding` with `{ skippedAt: now }`, invalidates the
 * cached state, and routes to `/`. The route guard in `App.tsx` sees the
 * non-null `skippedAt` and lets the operator through to the dashboard.
 *
 * Step screens are passed as `children` — the shell doesn't know about their
 * inner state machines.
 */
const STEPS: { key: OnboardingStep; label: string }[] = [
  { key: "survey", label: "About you" },
  { key: "explainer", label: "How it works" },
  { key: "generate_key", label: "API key" },
  { key: "done", label: "Done" },
];

export function OnboardingShell({
  current,
  children,
}: {
  current: OnboardingStep;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const skipMutation = useMutation({
    mutationFn: () =>
      apiClient.onboarding.patch({ skippedAt: new Date().toISOString() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["onboarding"] });
      navigate("/", { replace: true });
    },
  });

  const currentIdx = STEPS.findIndex((s) => s.key === current);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "var(--ad-surface-0)",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "20px 32px",
          borderBottom: "1px solid var(--ad-hairline)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Cascade size={26} />
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: 0.4,
              color: "var(--ad-fg-1)",
            }}
          >
            AuthenSee
          </span>
        </div>
        <button
          type="button"
          onClick={() => skipMutation.mutate()}
          disabled={skipMutation.isPending}
          style={{
            background: "transparent",
            border: 0,
            padding: 0,
            color: "var(--ad-fg-3)",
            fontSize: 12,
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          {skipMutation.isPending ? "Skipping…" : "Skip onboarding"}
        </button>
      </header>

      <main
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "40px 24px",
        }}
      >
        <div style={{ width: "100%", maxWidth: 640 }}>
          {/* Progress dots */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 32,
              justifyContent: "center",
            }}
          >
            {STEPS.map((s, i) => {
              const isCurrent = i === currentIdx;
              const isDone = i < currentIdx;
              return (
                <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span
                    aria-current={isCurrent ? "step" : undefined}
                    style={{
                      width: isCurrent ? 24 : 8,
                      height: 8,
                      borderRadius: 999,
                      background: isCurrent
                        ? "var(--rb-orange)"
                        : isDone
                          ? "rgba(255,123,26,0.4)"
                          : "var(--ad-hairline)",
                      transition: "width 160ms ease",
                    }}
                  />
                  {i < STEPS.length - 1 && (
                    <span
                      style={{
                        width: 12,
                        height: 1,
                        background: "var(--ad-hairline)",
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>

          {children}
        </div>
      </main>
    </div>
  );
}
